/**
 * Compact payment summary for Guided Note (PRD.161 §7.8 / PRD.218 §10).
 *
 * Shows charged / paid / balance for the encounter's invoices. PRD.218
 * makes invoice_payments the source of truth — `paid` is the sum of
 * actual payment rows, not a `status === 'paid'` shortcut. All money
 * math goes through Dinero via the @helios/domain helpers.
 */

import { useMemo } from 'react';
import { usePowerSyncQuery, usePowerSyncDb } from '../../../adapters/powersync';
import { qInvoicesByEncounter, type InvoiceRow, type InvoicePaymentRow } from '@helios/data-access';
import { useEffect, useState } from 'react';
import { isPositive, phpFromCents, phpFromMajor, subtractMoney, sumMoney } from '@helios/domain';
import { useFormatMoney } from '../../../hooks/useFormatMoney';

// ─── Props ───

interface Props {
  patientId: string;
  encounterId: string;
}

// ─── Component ───

export function GuidedPaymentSummary({ patientId, encounterId }: Props) {
  const { db } = usePowerSyncDb();
  const fmt = useFormatMoney();
  const { data: invoiceRows = [] } = usePowerSyncQuery<InvoiceRow>(
    qInvoicesByEncounter(patientId, encounterId),
  );

  // Pull all payments tied to any invoice for this encounter in one shot
  // so the summary updates live as new payments land. Skips when there
  // are no invoices yet so we don't issue an empty IN-clause query.
  const [paymentRows, setPaymentRows] = useState<InvoicePaymentRow[]>([]);
  useEffect(() => {
    let cancelled = false;
    if (!db || invoiceRows.length === 0) {
      setPaymentRows([]);
      return;
    }
    const invoiceIds = invoiceRows.map((inv) => inv.id);
    const placeholders = invoiceIds.map(() => '?').join(',');
    db.getAll<InvoicePaymentRow>(
      `SELECT * FROM invoice_payments WHERE invoice_id IN (${placeholders})`,
      invoiceIds,
    )
      .then((rows) => {
        if (!cancelled) setPaymentRows(rows);
      })
      .catch(() => {
        if (!cancelled) setPaymentRows([]);
      });
    return () => {
      cancelled = true;
    };
    // invoiceRows.length captures the changing invoice set without
    // re-running on every reference-identity change of the array.
  }, [db, invoiceRows, encounterId]);

  const { charged, paid, balance, isFullyPaid } = useMemo(() => {
    const totalCharged = sumMoney(invoiceRows.map((inv) => phpFromMajor(inv.grand_total ?? 0)));
    const totalPaid = sumMoney(paymentRows.map((p) => phpFromCents(p.amount_cents ?? 0)));
    const remaining = subtractMoney(totalCharged, totalPaid);
    return {
      charged: totalCharged,
      paid: totalPaid,
      balance: remaining,
      isFullyPaid: !isPositive(remaining) && isPositive(totalCharged),
    };
  }, [invoiceRows, paymentRows]);

  if (invoiceRows.length === 0) {
    return (
      <div
        style={{
          padding: 'var(--sp-5) var(--sp-6)',
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text-muted)',
          fontStyle: 'italic',
        }}
      >
        No payment activity for this visit
      </div>
    );
  }

  return (
    <div
      style={{
        padding: 'var(--sp-4) var(--sp-6)',
        display: 'flex',
        gap: 'var(--sp-5)',
        flexWrap: 'wrap',
      }}
    >
      <SummaryItem label="Charged" value={fmt(charged)} />
      <SummaryItem label="Paid" value={fmt(paid)} color="var(--color-success-text)" />
      <SummaryItem
        label="Balance"
        value={fmt(balance)}
        color={isFullyPaid ? 'var(--color-success-text)' : 'var(--color-warning-text)'}
      />
    </div>
  );
}

function SummaryItem({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
      <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>{label}</span>
      <span
        style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500, color: color ?? 'var(--text)' }}
      >
        {value}
      </span>
    </div>
  );
}
